import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { TAPDataStorageService } from '@ibe/services/mocks.tap';
import { BookedTrip, Flight, PassengersSegmentedCount } from '@ibe/models';
import * as moment from 'moment';

@Injectable({ providedIn: 'root' })
export class MybService {
	private readonly _today = new Date();

	// Flight booking steps
	today: BehaviorSubject<Date>;
	private booked_trips: BehaviorSubject<BookedTrip[]>;
	private booked_trips_is_loaded: BehaviorSubject<boolean>;
	imageToShow: any;
	isImageLoading: any;
	imageService: any;


	constructor(
		private _http: HttpClient,
		private router: Router,
		private sanitizer: DomSanitizer,
		private storage: TAPDataStorageService
	) {
		moment.locale(environment.locale);
		this._today = new Date();

		this.today = new BehaviorSubject(this._today);
		this.booked_trips = storage.observableBookings;
		this.booked_trips_is_loaded = storage.myBookingsMocksLoaded;
		this.storage.initializeBookingsIfEmpty();
	}

	get observableTrips() {
		return this.booked_trips;
	}

	get isBookedTripsLoaded() {
		return this.booked_trips_is_loaded;
	}

	get bookedTrips(): BookedTrip[] {
		return this.storage.bookedTrips;
	}

	get nextBookedTrip(): BookedTrip {
		const activeBookings = this.storage.bookedTrips.filter(bookedTrip =>
			bookedTrip.status === 'active');

		return activeBookings.reduce((bookedTrip, currentNextBooking) => bookedTrip.flights[0].departure.time < currentNextBooking.flights[0].departure.time ? bookedTrip : currentNextBooking);
	}

	get getTimeToThink(): BookedTrip {
		const activeBookings = this.storage.bookedTrips.filter(bookedTrip =>
			bookedTrip.status === 'pending');

		return activeBookings.reduce((bookedTrip, currentNextBooking) => bookedTrip.flights[0].departure.time < currentNextBooking.flights[0].departure.time ? bookedTrip : currentNextBooking);
	}


	get nextFlightsWithOpenCheckIn() {
		const activeBookings = this.storage.bookedTrips.filter(bookedTrip =>
			bookedTrip.status === 'active');

		let flights = [];
		activeBookings.forEach(function (bookedTrip) {
			bookedTrip.flights.forEach(function (flight) {
				flights.push(flight);
			});
		});

		const openCheckInFlights = flights.filter((flight: Flight) =>
			flight.check_in.status === 'open' && (flight.status === 'active' || flight.status === 'scheduled'));

		openCheckInFlights.sort((flights_1: Flight, flights_2: Flight) => {
			if (flights_1.check_in.time > flights_2.check_in.time) { return 1; }
			if (flights_1.check_in.time < flights_2.check_in.time) { return -1; }
			return 0;
		});

		const numberOfFlightsToReturn = openCheckInFlights.length > 2 ? 2 : openCheckInFlights.length;

		return openCheckInFlights.slice(0, numberOfFlightsToReturn);
	}

	getPassengers(trip: BookedTrip) {
		return trip.passengers_info;
	}

	/**
	 * Get booking from PNR
	 * @param {string} pnr of booking
	 * @returns booking trip
	 */
	getBooking(pnr: string): BookedTrip {
		return this.storage.getBookedTrip(pnr);
	}

	/**
	 * Update booking trip
	 * @param {string} pnr of booking
	 * @param {BookedTrip} updated_booked_trip bokking trip to update
	 * @returns booking trip
	 */
	updateBooking(pnr: string, updated_booked_trip: BookedTrip) {
		this.storage.updateBooking(pnr, updated_booked_trip);
	}

	/**
	 * Returns the Booking that contains the flight with the given flight id
	 * or undefined if none is found
	 * @param id - the flight identifier
	 */
	getBookingFromFlightId(id: string): BookedTrip | undefined {
		if (this.booked_trips_is_loaded.getValue()) {
			return this.storage.bookedTrips.find((bookedTrip) => {
				return bookedTrip.flights.find(flight => flight.id === id) !== undefined;
			});
		} else {
			return undefined;
		}
	}

	/**
	 * Get number of days left from today until a date
	 * @param {Date} date to compare with today
	 * @returns number of days left to date
	 */
	getTimeLeft(date: Date): string {
		const oneDayTime = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
		const timeLeft =
			Math.round(
				Math.abs((date.getTime() - this._today.getTime() + 1) / oneDayTime)
			) + 1;

		if (timeLeft > 0) {
			return 'In ' + timeLeft + ' days';
		}
	}

	/**
	 * Get booking type text formated
	 * @param {String} booking_type to return
	 * @returns booking type text
	 */
	getBookingType(booking_type: String): string {
		switch (booking_type) {
			case 'round trip':
				return 'Round trip';
			case 'one way':
				return 'One way';
			case 'multicity':
				return 'Multicity';
			case 'stopover':
				return 'Stopover';
			default:
				return;
		}
	}

	daysCountBetween(timeStart, timeEnd) {
		timeStart = new Date(timeStart);
		timeEnd = new Date(timeEnd);
		const daysCount = Math.floor(
			(Date.UTC(timeEnd.getFullYear(), timeEnd.getMonth(), timeEnd.getDate()) -
				Date.UTC(
					timeStart.getFullYear(),
					timeStart.getMonth(),
					timeStart.getDate()
				)) /
			(1000 * 60 * 60 * 24)
		);
		return daysCount;
	}

	formatDate(date: Date): string {
		return moment(date).format('DD MMM YYYY');
	}

	/**
	 * Get booking dates text formated
	 * @param {Flight[]} flights to get and return dates
	 * @returns booking dates text formated
	 */
	getFlightDates(flights: Flight[]): string {
		if (flights.length === 0) {
			return '----';
		}

		if (flights.length > 1) {
			// Roundtrip, Multicity
			let departureDate;
			// if (
			// 	// If departure and return on same year
			// 	flights[0].departure.time.getFullYear() ===
			// 	flights[flights.length - 1].arrival.time.getFullYear()
			// ) {
			if (
				// If departure and return on same month
				moment(flights[0].departure.time).month() ===
				moment(flights[flights.length - 1].departure.time).month()
			) {
				departureDate = moment(flights[0].departure.time).date();
			} else {
				// If departure and return on same year and different month
				departureDate = moment(flights[0].departure.time).format('DD MMM');
			}
			// } else {
			// 	// If departure and return on diferent year
			// 	departureDate = this.formatDate(flights[0].departure.time);
			// }
			return (
				departureDate +
				' - ' +
				this.formatDate(flights[flights.length - 1].departure.time)
			);
		} else {
			// Oneway
			return this.formatDate(flights[0].departure.time);
		}
	}

	/**
	 * Get number of trip days
	 * @param {Array<Flight>} flights to get from dates
	 * @returns total trip days
	 */
	getDatesInterval(flights: Array<Flight>): number {
		if (flights.length === 0) {
			return 0;
		}

		const departureTime = moment(flights[0].departure.time);
		const returnTime = moment(flights[flights.length - 1].arrival.time);

		return Math.ceil(moment.duration(returnTime.diff(departureTime)).asDays());
	}

	/**
	 * Get total number of passengers
	 * @param passengers list of passengers by type
	 * @returns total number of passengers
	 */
	getTotalPassengers(passengers: PassengersSegmentedCount): number {
		return passengers.adults + passengers.children + passengers.infants;
	}

	/**
	 * Get booking total price
	 * @param {Flight[]} flights to get from dates
	 * @returns booking total price
	 */
	getPrice(flights: Array<Flight>): String {
		let totalPrice = 0;
		for (const flight of flights) {
			totalPrice += flight.options.price;
		}

		return totalPrice + ' ' + environment.currency.symbol;
	}

	/**
	 * Get text formated of passengers by type
	 * @param passengers list of passengers by type
	 * @returns text formated of passengers by type
	 */
	getPassengersByType(passengers): String {
		const passengersByType = [];
		if (passengers.adults !== 0) {
			passengersByType.push(passengers.adults + ' adults');
		}
		if (passengers.children !== 0) {
			passengersByType.push(passengers.children + ' children');
		}
		if (passengers.infants !== 0) {
			passengersByType.push(passengers.infants + ' infants');
		}

		return passengersByType.join(', ');
	}


	/**
	 * Get destination banner image from URL
	 * @param destination Name to use on URL
	 * @returns sanitized background-image style
	 */
	printDestinationImage(destination: String) {
		const url =
			'url(./assets/images/destinations/banner/' +
			destination.toUpperCase() +
			'.jpg)';
		const destinationImageUrl = this.sanitizer.bypassSecurityTrustStyle(url);
		return destinationImageUrl;
	}

	/**
	 * Get destination logo in SVG image from URL
	 * @param destination Name to use on URL
	 * @returns sanitized logo url
	 */
	printDestinationLogo(destination: String) {
		const lang = 'EN'; // Example market
		const url = './assets/images/destinations/logo/' + destination.toUpperCase() + '/' + lang + '.svg';
		const destinationLogoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
			url
		);
		return destinationLogoUrl;
	}

	/**
	 * Add PNR's trip from "Find existing booking" to active bookings list
	 * @param pnr PNR of trip to add to My Bookings
	 */
	addBookingToAccount(pnr) {
		this.storage.addBookingToAccount(pnr);
	}
}
